import { CheckRounded, KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAsyncAction, useStateRef } from 'app/services';
import { transportGroupTypeFormLoad } from 'app/store/transport';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

const TransportGroupForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const [color, setColor] = useState(defaultValues ? defaultValues.color : theme.palette.primary.main);

  const { asyncAction: fetchTypeData, isLoading: isFetchTypeLoading } = useAsyncAction(transportGroupTypeFormLoad);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { defaultValues, onSubmit } = props;
  const { groupTypeItems } = useSelector((state) => state.transport);
  const { reset, control, register, setValue, formState, handleSubmit } = useForm({
    defaultValues: {
      name: defaultValues ? defaultValues.name : '',
      color: defaultValues ? defaultValues.color : theme.palette.primary.main,
      type_id: defaultValues ? defaultValues.type_id : '',
    },
  });

  const handleColorChange = (value) => {
    setColor(value);
    setValue('color', value, { shouldDirty: true });
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          name: data.name,
          color: data.color,
          type_id: parseInt(data.type_id),
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        color: defaultValues.color,
        type_id: defaultValues.type_id,
      });

      setColor(defaultValues.color);
    }
  }, [defaultValues]);

  useEffect(() => {
    fetchTypeData();
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.name} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Название</InputLabel>
            <OutlinedInput
              label='Название'
              variant='outlined'
              notched={firstUpdate}
              fullWidth
              placeholder='Название группы'
              {...register('name', {
                pattern: /^([A-Za-zА-Яа-я0-9_\-\s]{3,255})$/iu,
                required: true,
                minLength: 3,
              })}></OutlinedInput>
            <FormHelperText>Используйте в названии не менее 3 символов</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.role_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Тип группы</InputLabel>
            {isFetchTypeLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchTypeLoading && (
              <Controller
                name='type_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Тип группы'
                    defaultValue=''
                    IconComponent={KeyboardArrowDownRounded}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      required: true,
                      pattern: /^([0-9]{1,11})?$/iu,
                    })}
                    notched={firstUpdate}
                    value={field.value}
                    onChange={field.onChange}>
                    {groupTypeItems.map((value, key) => (
                      <MenuItem key={key} disableRipple value={value.id}>
                        {value.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
            <FormHelperText>Выберите тип группы транспортных средств в системе</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl
            error={!!formState.errors.color}
            margin='normal'
            variant='outlined'
            fullWidth
            sx={(theme) => ({
              marginBottom: theme.spacing(0),
              '.MuiOutlinedInput-notchedOutline': {
                borderBottom: '0',
                borderBottomLeftRadius: '0',
                borderBottomRightRadius: '0',
              },
              '.MuiOutlinedInput-root': {
                color: 'text.primary',
              },
              '.MuiOutlinedInput-input': {
                WebkitTextFillColor: 'currentColor',
              },
            })}>
            <InputLabel disableAnimation>Цвет маркера</InputLabel>
            <OutlinedInput label='Цвет маркера' variant='outlined' value={color} disabled notched fullWidth placeholder='Цвет маркера' />
          </FormControl>
          <FormControl
            margin='normal'
            variant='outlined'
            fullWidth
            sx={(theme) => ({
              marginTop: '0',
              '.react-colorful': {
                width: '100%',
                borderBottomLeftRadius: theme.shape.borderRadius * 2,
                borderBottomRightRadius: theme.shape.borderRadius * 2,
              },
              '.react-colorful__saturation': {
                boxShadow: 'inset 0px -1px 0px 1px rgb(0 0 0 / 15%)',
                borderRadius: '0',
              },
              '.react-colorful__last-control': {
                borderRadius: '0',
              },
            })}>
            <HexColorPicker color={color} onChange={handleColorChange} />
            <Stack
              direction='row'
              sx={(theme) => ({
                padding: theme.spacing(0.75),
                backgroundColor: 'background.default',
                borderBottomLeftRadius: theme.shape.borderRadius * 2,
                borderBottomRightRadius: theme.shape.borderRadius * 2,
              })}>
              {config.transportGroupPresetColors.map((value, key) => (
                <Box
                  component='div'
                  key={key}
                  onClick={() => handleColorChange(value)}
                  sx={{
                    width: '24px',
                    height: '24px',
                    margin: '4px',
                    border: 'none',
                    padding: '0',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: value,
                    ...(color !== value && {
                      svg: {
                        display: 'none',
                        filter: 'invert(1)',
                      },
                    }),
                  }}>
                  <CheckRounded fontSize='small' />
                </Box>
              ))}
            </Stack>
            <FormHelperText>
              Выберите цвет из палитры или предложенных вариантов, который будет использоваться для отображения цвета маркера на карте.
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
});

export default TransportGroupForm;
